import React, { useMemo, useCallback, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Cell } from 'recharts';
// Make sure DailyData is exported from '../../types/dashboard', or replace with the correct type name
// Replace with the correct type if available, or define inline:
export interface DailyData {
  date: string;
  pnl: number;
  trades: number;
  // Add other fields if necessary
}
// If a type is exported from '../../types/dashboard', use:
// import type { ExistingType } from '../../types/dashboard';
import { GREEN, RED } from '../../constants';
import { fmtMoney, fmtMonthDay, fmtMoneyWithDecimals, fmtTooltipDate } from '../../utils/formatters';
import { ChartFrame } from '../charts/ChartFrame';
import { buildCanonicalDailyData } from '../../lib/trades/tradeLevelStats';
import { filterCanonicalRealizedTrades } from '../../lib/trades/dashboardTradeSource';

interface AverageTradePnlWidgetProps {
  dailyData: DailyData[];
  trades?: any[];
  isDark: boolean;
}

const CustomTooltip = ({ active, payload, label, formatter }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const value = data.value;
    const isGain = value >= 0;
    const labelText = isGain ? "Gains:" : "Loss:";
    const formatted = formatter ? formatter(value, data.name) : [`${labelText} `, value];
    return (
      <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg p-2 text-sm">
        <p className="font-semibold text-zinc-600 dark:text-zinc-300">{fmtTooltipDate(label)}</p>
        <p style={{ color: data.payload.fill || data.color || (isGain ? GREEN : RED) }}>
          {Array.isArray(formatted) ? `${formatted[0]}${formatted[1]}` : formatted}
        </p>
      </div>
    );
  }
  return null;
};

export const AverageTradePnlWidget: React.FC<AverageTradePnlWidgetProps> = React.memo(({ dailyData, trades, isDark }) => {
  const gridColor = isDark ? "#404040" : "#e5e7eb";
  const textColor = isDark ? "#a1a1aa" : "#6b7280";

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  // Canonical source of truth: when the authoritative trade records are provided,
  // the daily bars are rebuilt from the canonical closed trades (the same
  // filter + daily aggregation the dashboard uses). This guarantees the widget
  // renders the same realized P&L as every other performance widget and is never
  // empty merely because the dashboard-range dailyData is empty.
  const canonicalDailyData = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) return null;
    const realized = filterCanonicalRealizedTrades(trades);
    if (realized.length === 0) return null;
    return buildCanonicalDailyData(trades, 366);
  }, [trades]);

  // Show only the most recent 7 days
  const chartData = useMemo(() => {
    const source = canonicalDailyData ?? dailyData;
    const recentData = source.slice(-7);
    return recentData.map(d => ({
      ...d,
      avgPnl: d.trades > 0 ? d.pnl / d.trades : 0,
    }));
  }, [canonicalDailyData, dailyData]);

  // Use computed chart data directly (remove any temporary test overrides)
  const testData = chartData;
  const hasData = testData.length > 0;

  // Show a label under every bar; with 7 bars max, interval=0 ensures no skipping.

  // Match shared chart margins used elsewhere to avoid clipping/misalignment of top tick labels
  const margin = useMemo(() => ({ top: 8, right: 7, left: -4, bottom: 10 }), []);
  // Gap in pixels between the chart axis and the numeric label's right edge
  // Set to 4px so the plotting area stays 4px away from labels.
  const LABEL_GAP = 4;
  // Small offset (px) to nudge labels horizontally when needed (0 = no nudge).
  // Use negative values to shift labels left. Set to -2 to nudge another 1px left.
  // No extra horizontal nudge; keep labels aligned to the axis gap precisely

  // Header alignment measurement removed — labels are anchored to the axis with a fixed gap.

  // Compute max label width (estimate using canvas) from min/max values so we can
  // reserve exactly enough Y-axis width and flush the chart left to the labels.
  const yAxisWidth = useMemo(() => {
    try {
  const values = (chartData || []).map(d => d.avgPnl ?? 0);
  if (values.length === 0) return 32;
  // Measure all labels (not only min/max) to ensure we reserve enough
  // space for any tick that might be rendered and avoid clipping.
  const labels = values.map(v => fmtMoneyWithDecimals(v, 0));
  // also include 0 and extremes just in case
  labels.push(fmtMoneyWithDecimals(0, 0));
      const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
      const ctx = canvas ? canvas.getContext('2d') : null;
      let maxW = 0;
      if (ctx) {
        ctx.font = '12px sans-serif';
        for (const l of labels) {
          const w = ctx.measureText(l).width;
          if (w > maxW) maxW = w;
        }
      } else {
        // fallback estimation
        for (const l of labels) {
          const w = l.length * 6;
          if (w > maxW) maxW = w;
        }
      }
      // add LABEL_GAP and small padding; reserve exactly enough width so the
      // chart area will adjust based on label width. Avoid using header offset
      // here so the reserved width is strictly the label width + gap.
  const PADDING = 0;
  const LEFT_SAFE_PAD = 0;
  // Reserve exactly enough width: max label width + small gap to axis
  const reserved = Math.ceil(maxW) + LEFT_SAFE_PAD + LABEL_GAP + PADDING;
    // We anchor labels to the chart axis (not header), so no header-based minimum.
    return Math.max(reserved, 32);
    } catch {
      return 32;
    }
  }, [chartData]);

  const xTickStyle = useMemo(() => ({ fontSize: 12, fill: textColor }), [textColor]);
  
  const xTickFormatter = useCallback((value: string) => fmtMonthDay(value), []);
  const tooltipContent = useMemo(() => (
    <CustomTooltip formatter={(value: number) => {
      const isGain = value >= 0;
      const labelText = isGain ? 'Gains:' : 'Loss:';
      return [labelText, fmtMoney(value)];
    }} />
  ), []);

  // Adjusted Y-axis label alignment using measured header-left offset
  const renderYTick = useCallback(({ y, payload }: any) => {
    const value = payload?.value as number | undefined;
    const formatted = (typeof value === 'number') ? fmtMoneyWithDecimals(value, 0) : String(value ?? '');

    const fill = isDark ? '#9ca3af' : '#374151';
    // Start labels at the same left edge as the divider. The Y-axis width above
    // still reserves space for them before the plotting area begins.
    const labelLeftX = 0;
    // Pixel-align the vertical position to avoid subpixel blur and ensure
    // the label vertically centers on the tick line.
    let yAligned = y;
    try {
      if (typeof window !== 'undefined') {
        const dpr = window.devicePixelRatio || 1;
        yAligned = Math.round(y * dpr) / dpr;
      } else {
        yAligned = Math.round(y);
      }
    } catch {}
    return (
      <text x={labelLeftX} y={yAligned} fill={fill} fontSize={12} textAnchor="start" dominantBaseline="middle">
        {formatted}
      </text>
    );
  }, [chartData, isDark, margin]);

  return (
    <div className="h-full flex flex-col">
      {hasData ? (
        <div className="flex-1">
          <ChartFrame>
            {({ width, height }: { width: number; height: number }) => (
              <BarChart width={width} height={height} data={testData} margin={margin}>
                <CartesianGrid vertical={false} stroke={gridColor} />
                <XAxis dataKey="date" interval={0} allowDuplicatedCategory={true} height={36} tickMargin={12} tick={xTickStyle as any} tickFormatter={xTickFormatter} />
                <YAxis width={yAxisWidth} tickFormatter={(v) => fmtMoneyWithDecimals(v as number, 0)} tick={renderYTick} domain={['auto', 'auto']} allowDataOverflow={true} axisLine={true} tickLine={true} tickMargin={0} />
                <Tooltip
                  cursor={false}
                  offset={6}
                  allowEscapeViewBox={{ x: false, y: false }}
                  wrapperStyle={{ zIndex: 50 }}
                  content={tooltipContent as any}
                  labelFormatter={(value: any) => (typeof value === 'string' ? fmtTooltipDate(value) : '')}
                />
                <ReferenceLine y={0} stroke={textColor} />
                <Bar
                  dataKey="avgPnl"
                  onMouseOver={(_, idx: number | undefined) => setHoverIndex(typeof idx === 'number' ? idx : null)}
                  onMouseOut={() => setHoverIndex(null)}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={hoverIndex === index ? 'rgba(161,161,170,0.25)' : entry.avgPnl >= 0 ? GREEN : RED}
                      cursor="pointer"
                    />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ChartFrame>
        </div>
      ) : (
        <div className="h-full flex items-center justify-center rounded-xl border border-dashed border-zinc-200 text-sm text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
          Import Trades to see the widget
        </div>
      )}
    </div>
  );
}, (prev, next) => {
  if (prev.isDark !== next.isDark) return false;
  if (prev.trades !== next.trades) return false;
  const a = prev.dailyData, b = next.dailyData;
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const la = a[a.length - 1]?.date;
  const lb = b[b.length - 1]?.date;
  return la === lb;
});
